import React, { useEffect, useRef } from 'react';

const PixieDustAnimation = ({ onComplete, duration = 8000 }) => {
  
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const particlesRef = useRef([]);
  const fireworksRef = useRef([]);
  const streaksRef = useRef([]);
  const flashRef = useRef({ a: 0, x: 0, y: 0, r: 0 });

  // Constants
  const TAU = Math.PI * 2;
  const gravity = 0.12;
  const air = 0.999;
  
  // White palette for fireworks and particles
  const whitePalette = ['#FFFFFF', '#F8F8F8', '#F0F0F0', '#E8E8E8', '#E0E0E0'];
  const particleWhitePalette = ['#FFFFFF', '#F8F8F8', '#F0F0F0', '#E8E8E8', '#E0E0E0'];

  // Utility functions
  const clamp = (n, a, b) => Math.max(a, Math.min(b, n));
  const rand = (a, b) => a + Math.random() * (b - a);
  const chance = p => Math.random() < p;

  const rgba = (hex, a = 1) => {
    const h = hex.replace('#', '');
    const n = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
    const num = parseInt(n, 16);
    const r = (num >> 16) & 255;
    const g = (num >> 8) & 255;
    const b = num & 255;
    return `rgba(${r},${g},${b},${a})`;
  };

  const shuffle = (arr) => {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  };

  const pick = (vals, weights) => {
    const sum = weights.reduce((s, w) => s + w, 0);
    let r = Math.random() * sum;
    for (let i = 0; i < vals.length; i++) {
      if ((r -= weights[i]) <= 0) return vals[i];
    }
    return vals[0];
  };

  // Fireworks Streak class
  class FireworksStreak {
    constructor(x1, y1, x2, y2, color, life = 560, width = 1.5) {
      this.x1 = x1;
      this.y1 = y1;
      this.x2 = x2;
      this.y2 = y2;
      this.life = life;
      this.max = life;
      this.color = color;
      this.width = width;
    }

    step(dt) {
      this.life -= dt * 1000;
      return this.life > 0;
    }

    draw(c) {
      const a = Math.max(0, Math.min(1, this.life / this.max));
      c.globalCompositeOperation = 'lighter';
      const g = c.createLinearGradient(this.x1, this.y1, this.x2, this.y2);
      const alpha = 0.48 * a;
      g.addColorStop(0, this.color.replace('ALPHA', '0'));
      g.addColorStop(0.35, this.color.replace('ALPHA', alpha.toFixed(3)));
      g.addColorStop(1, this.color.replace('ALPHA', '0'));
      c.strokeStyle = g;
      c.lineWidth = this.width * (0.8 + 0.2 * a);
      c.beginPath();
      c.moveTo(this.x1, this.y1);
      c.lineTo(this.x2, this.y2);
      c.stroke();
    }
  }

  // Fireworks Particle class
  class FireworksParticle {
    constructor(x, y, vx, vy, color, type, sparkle) {
      this.x = x;
      this.y = y;
      this.vx = vx;
      this.vy = vy;
      this.color = color;
      this.type = type;
      this.sparkle = sparkle;
      this.life = rand(1.7, 2.7);
      this.age = 0;
      this.size = rand(1.0, 1.6);
      this.lastX = x;
      this.lastY = y;
    }

    step(dt) {
      this.age += dt;
      this.lastX = this.x;
      this.lastY = this.y;
      this.vx *= air;
      this.vy *= air;
      this.vy += gravity * dt * 0.82;
      
      if (this.type === 'willow') {
        this.vx *= 0.997;
        this.vy += 0.01;
      }
      
      this.x += this.vx * dt * 60;
      this.y += this.vy * dt * 60;

      if (chance(0.22)) {
        streaksRef.current.push(new FireworksStreak(
          this.lastX, this.lastY, this.x, this.y,
          rgba(this.color, 0.3), 560, 1.1
        ));
      }
      
      return this.age < this.life && this.x > -20 && this.x < canvasRef.current.width + 20 && this.y < canvasRef.current.height + 20;
    }

    draw(c) {
      const p = 1 - (this.age / this.life);
      const a = Math.max(0, Math.min(0.7, 0.12 + p * 0.6));
      c.globalCompositeOperation = 'lighter';

      // Warm white halo
      c.fillStyle = rgba(this.color, 0.05 + 0.16 * a);
      c.beginPath();
      c.arc(this.x, this.y, this.size * 4.0, 0, TAU);
      c.fill();

      // Core dot
      const sparkle = this.sparkle ? (1 + Math.sin(this.age * 50) * 0.12) : 1;
      c.fillStyle = rgba(this.color, 0.78);
      c.beginPath();
      c.arc(this.x, this.y, Math.max(0.8, this.size * 0.95 * sparkle), 0, TAU);
      c.fill();
    }
  }

  // Flash function
  const flashAt = (x, y, power) => {
    flashRef.current.a = Math.min(0.24, flashRef.current.a + power);
    flashRef.current.x = x;
    flashRef.current.y = y;
    flashRef.current.r = 420;
  };

  // Pixie Dust Particle class with spiral-elliptical path
  class PixieDustParticle {
    constructor() {
      // Center point for spiral convergence
      this.centerX = window.innerWidth / 2;
      this.centerY = window.innerHeight / 2;
      
      // Start position (random around edges)
      this.startAngle = Math.random() * TAU;
      this.startRadius = Math.max(window.innerWidth, window.innerHeight) * 0.8;
      this.startX = this.centerX + this.startRadius * Math.cos(this.startAngle);
      this.startY = this.centerY + this.startRadius * Math.sin(this.startAngle);
      
      // Animation timing
      this.startTime = 0;
      this.duration = 2500 + Math.random() * 800; // 2.5-3.3 seconds
      this.delay = Math.random() * 400; // Staggered start times
      
      // Particle properties
      this.size = 1.5 + Math.random() * 2.5; // 1.5-4px
      this.opacity = 0;
      this.trailLength = 20 + Math.random() * 30; // 20-50 particles in trail
      
      // Spiral parameters
      this.spiralTurns = 1.5 + Math.random() * 1; // 1.5-2.5 turns
      this.spiralTightness = 0.3 + Math.random() * 0.4; // How tight the spiral is
      
      // Trail effect
      this.trail = [];
      this.maxTrailLength = Math.floor(this.trailLength);
      
      // Glow effect
      this.glowSize = this.size * 4;
      
      // Current position
      this.x = this.startX;
      this.y = this.startY;
      
      // Twinkling effect
      this.twinkleSpeed = 0.02 + Math.random() * 0.03;
      this.twinklePhase = Math.random() * Math.PI * 2;
      
      // Track if this particle should trigger fireworks
      this.willTriggerFireworks = Math.random() < 0.15; // 15% chance
      this.fireworksTriggered = false;
    }

    update(currentTime) {
      if (currentTime < this.delay) return;
      
      const elapsed = currentTime - this.delay;
      const progress = Math.min(elapsed / this.duration, 1);
      
      // Calculate spiral path - particles spiral inward toward center
      const angle = this.startAngle + progress * this.spiralTurns * TAU;
      const radius = this.startRadius * (1 - progress) * this.spiralTightness + (1 - progress) * this.startRadius * (1 - this.spiralTightness);
      
      // Add elliptical variation
      const ellipseX = radius * 1.2; // Slightly wider
      const ellipseY = radius * 0.8; // Slightly narrower
      
      // Update position
      this.x = this.centerX + ellipseX * Math.cos(angle);
      this.y = this.centerY + ellipseY * Math.sin(angle);
      
      // Update opacity with twinkling effect
      const baseOpacity = progress < 0.1 ? progress / 0.1 : (progress > 0.85 ? (1 - progress) / 0.15 : 1);
      const twinkle = 0.7 + 0.3 * Math.sin(currentTime * this.twinkleSpeed + this.twinklePhase);
      this.opacity = baseOpacity * twinkle;
      
      // Add current position to trail
      this.trail.push({
        x: this.x,
        y: this.y,
        opacity: this.opacity,
        size: this.size,
        twinkle: twinkle
      });
      
      // Limit trail length
      if (this.trail.length > this.maxTrailLength) {
        this.trail.shift();
      }
      
      // Update trail opacity (fade from front to back)
      this.trail.forEach((point, index) => {
        const trailProgress = index / this.trail.length;
        point.opacity = this.opacity * (1 - trailProgress * 0.9) * point.twinkle;
      });
      
      // Trigger fireworks explosion when particle reaches center
      if (progress > 0.95 && this.willTriggerFireworks && !this.fireworksTriggered) {
        this.triggerFireworks();
        this.fireworksTriggered = true;
      }
    }

    triggerFireworks() {
      const baseCount = 90 + Math.floor(rand(0, 60));
      const count = Math.floor(baseCount * 1.0); // More fireworks
      const type = pick(['sphere', 'peony', 'chrys', 'ring'], [0.4, 0.35, 0.15, 0.1]);
      const pal = shuffle([...whitePalette]);
      
      for (let i = 0; i < count; i++) {
        const angle = Math.random() * TAU;
        let speed = rand(2.0, 3.6) * 0.8; // Slower spread for elegance
        let vx = Math.cos(angle) * speed;
        let vy = Math.sin(angle) * speed;
        
        if (type === 'ring') {
          const t = (i / count) * TAU;
          vx = Math.cos(t) * speed * 1.08;
          vy = Math.sin(t) * speed * 1.08;
        }
        if (type === 'chrys') {
          vx *= 1.04;
          vy *= 1.04;
        }
        
        const col = pal[i % pal.length];
        fireworksRef.current.push(new FireworksParticle(
          this.x, this.y, vx, vy, col, type, chance(0.25)
        ));
      }
      
      flashAt(this.x, this.y, 0.12); // Moderate flash
    }

    draw(ctx) {
      if (this.opacity <= 0 || this.trail.length === 0) return;
      
      // Draw trail first (so it appears behind the main particle)
      this.trail.forEach((point, index) => {
        if (point.opacity <= 0) return;
        
        ctx.save();
        ctx.globalAlpha = point.opacity;
        
        // Create magical white glow effect
        const gradient = ctx.createRadialGradient(
          point.x, point.y, 0,
          point.x, point.y, this.glowSize
        );
        gradient.addColorStop(0, 'rgba(255, 255, 255, 1)'); // Pure White
        gradient.addColorStop(0.2, 'rgba(255, 255, 255, 0.9)');
        gradient.addColorStop(0.5, 'rgba(255, 255, 255, 0.6)');
        gradient.addColorStop(0.8, 'rgba(255, 255, 255, 0.2)');
        gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
        
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(point.x, point.y, this.glowSize, 0, Math.PI * 2);
        ctx.fill();
        
        // Draw bright white core particle
        ctx.fillStyle = 'rgba(255, 255, 255, 0.95)'; // Pure White
        ctx.beginPath();
        ctx.arc(point.x, point.y, point.size, 0, Math.PI * 2);
        ctx.fill();
        
        // Add sparkle effect for some particles
        if (Math.random() < 0.3) {
          ctx.fillStyle = 'rgba(255, 255, 255, 0.9)'; // White sparkle for contrast
          ctx.beginPath();
          ctx.arc(point.x, point.y, point.size * 0.3, 0, Math.PI * 2);
          ctx.fill();
        }
        
        ctx.restore();
      });
    }

    isComplete(currentTime) {
      return currentTime > this.delay + this.duration;
    }
  }

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    const ctx = canvas.getContext('2d');
    let animationId;
    let startTime = null;

    // Set canvas size
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Create pixie dust particles
    const createParticles = () => {
      particlesRef.current = [];
      const particleCount = 25 + Math.floor(Math.random() * 20); // 25-45 particles
      
      for (let i = 0; i < particleCount; i++) {
        particlesRef.current.push(new PixieDustParticle());
      }
    };

    // Animation loop
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      
      const elapsed = currentTime - startTime;
      const dt = Math.min(0.033, (currentTime - (animate.lastTime || currentTime)) / 1000);
      animate.lastTime = currentTime;
      
      // Clear canvas with transparent background
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Update and draw pixie dust particles
      particlesRef.current.forEach((particle, index) => {
        particle.update(elapsed);
        particle.draw(ctx);
        
        // Remove completed particles
        if (particle.isComplete(elapsed)) {
          particlesRef.current.splice(index, 1);
        }
      });

      // Update and draw fireworks particles
      for (let i = fireworksRef.current.length - 1; i >= 0; i--) {
        const p = fireworksRef.current[i];
        if (!p.step(dt)) {
          fireworksRef.current.splice(i, 1);
        } else {
          p.draw(ctx);
        }
      }

      // Update and draw streaks
      for (let i = streaksRef.current.length - 1; i >= 0; i--) {
        const s = streaksRef.current[i];
        if (!s.step(dt)) {
          streaksRef.current.splice(i, 1);
          continue;
        }
        s.draw(ctx);
      }

      // Flash effect - white explosion
      if (flashRef.current.a > 0.002) {
        const grad = ctx.createRadialGradient(
          flashRef.current.x, flashRef.current.y, 0,
          flashRef.current.x, flashRef.current.y, flashRef.current.r
        );
        grad.addColorStop(0, `rgba(255,255,255,${flashRef.current.a})`); // Pure white flash
        grad.addColorStop(0.5, `rgba(248,248,248,${flashRef.current.a * 0.7})`); // Light white
        grad.addColorStop(1, 'rgba(255,255,255,0)');
        ctx.globalCompositeOperation = 'lighter';
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        flashRef.current.a *= 0.94;
      }
      
      // Check if animation is complete
      if (elapsed >= duration) {
        cancelAnimationFrame(animationId);
        if (onComplete) {
          onComplete(); // Call immediately when animation completes
        }
        return;
      }
      
      // Continue animation
      animationId = requestAnimationFrame(animate);
    };

    // Initialize
    createParticles();
    animationId = requestAnimationFrame(animate);

    // Cleanup
    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', resizeCanvas);
    };
  }, [duration, onComplete]);

  return (
    <div className="fixed inset-0 z-modal pointer-events-none" style={{ background: '#000000' }}>
      <canvas
        ref={canvasRef}
        className="w-full h-full"
        style={{ background: 'transparent' }}
      />
    </div>
  );
};

export default PixieDustAnimation;
